import type { SearchFilters, PaginationConfig } from '@content-search/domain';
import { emptySearchFilters, emptyPanelFilters, toISOString, fromISOString, isDefaultDateRange } from '@content-search/utils';

/**
 * URL parameter keys for search filters
 */
const URL_PARAM_KEYS = {
  // Basic search fields
  userId: 'userId',
  subjectId: 'subjectId',

  // Panel filters
  contentTypes: 'contentTypes',
  labels: 'labels',
  languages: 'languages',
  topics: 'topics',
  startDate: 'startDate',
  endDate: 'endDate',
  isSampled: 'isSampled',
  policyFilter: 'policyFilter',

  // Pagination
  page: 'page',
  pageSize: 'pageSize',

  // Metadata (serialized as JSON)
  metadata: 'metadata',
} as const;

/**
 * Serialize search filters to URL search parameters
 */
// eslint-disable-next-line complexity, max-statements
export function serializeFiltersToUrl(filters: SearchFilters, pagination: PaginationConfig): URLSearchParams {
  const params = new URLSearchParams();

  // Basic search fields - only add if they have non-empty values
  if (filters.userId && filters.userId.trim() !== '') {
    params.set(URL_PARAM_KEYS.userId, filters.userId);
  }

  if (filters.subjectId && filters.subjectId.trim() !== '') {
    params.set(URL_PARAM_KEYS.subjectId, filters.subjectId);
  }

  // Panel filters - arrays
  if (filters.panelFilters.contentTypes.length > 0) {
    params.set(URL_PARAM_KEYS.contentTypes, filters.panelFilters.contentTypes.join(','));
  }

  if (filters.panelFilters.labels.length > 0) {
    params.set(URL_PARAM_KEYS.labels, filters.panelFilters.labels.join(','));
  }

  if (filters.panelFilters.languageDetected && filters.panelFilters.languageDetected !== 'all') {
    params.set(URL_PARAM_KEYS.languages, filters.panelFilters.languageDetected);
  }

  if (filters.panelFilters.topics.length > 0) {
    params.set(URL_PARAM_KEYS.topics, filters.panelFilters.topics.join(','));
  }

  // Panel filters - date range (only include in URL if not default range)
  if (filters.panelFilters.startDate && filters.panelFilters.endDate && !isDefaultDateRange(filters.panelFilters.startDate, filters.panelFilters.endDate)) {
    const utcStartDate = toISOString(filters.panelFilters.startDate);
    const utcEndDate = toISOString(filters.panelFilters.endDate);

    if (utcStartDate) {
      params.set(URL_PARAM_KEYS.startDate, utcStartDate);
    }

    if (utcEndDate) {
      params.set(URL_PARAM_KEYS.endDate, utcEndDate);
    }
  }

  // Only include sampling in URL if it's not the default value (sampled = true)
  if (filters.panelFilters.isSampled !== undefined && filters.panelFilters.isSampled !== true) {
    params.set(URL_PARAM_KEYS.isSampled, filters.panelFilters.isSampled.toString());
  }

  // Policy filter - only include if not default (undefined means 'all')
  if (filters.panelFilters.policyFilter) {
    params.set(URL_PARAM_KEYS.policyFilter, filters.panelFilters.policyFilter);
  }

  // Metadata (serialize as JSON)
  if (filters.panelFilters.metadata && Object.keys(filters.panelFilters.metadata).length > 0) {
    params.set(URL_PARAM_KEYS.metadata, JSON.stringify(filters.panelFilters.metadata));
  }

  // Pagination
  if (pagination.currentPage > 1) {
    params.set(URL_PARAM_KEYS.page, pagination.currentPage.toString());
  }

  if (pagination.pageSize !== 25) {
    // Default page size is 25
    params.set(URL_PARAM_KEYS.pageSize, pagination.pageSize.toString());
  }

  return params;
}

/**
 * Deserialize URL search parameters to search filters
 */
// eslint-disable-next-line complexity, max-statements
export function deserializeFiltersFromUrl(searchParams: URLSearchParams): {
  filters: SearchFilters;
  pagination: PaginationConfig;
} {
  const filters: SearchFilters = {
    ...emptySearchFilters,
    panelFilters: { ...emptyPanelFilters },
  };

  const pagination: PaginationConfig = {
    currentPage: 1,
    pageSize: 25,
  };

  // Basic search fields
  const userId = searchParams.get(URL_PARAM_KEYS.userId);

  if (userId) {
    filters.userId = userId;
  }

  const subjectId = searchParams.get(URL_PARAM_KEYS.subjectId);

  if (subjectId) {
    filters.subjectId = subjectId;
  }

  // Panel filters - arrays
  const contentTypes = searchParams.get(URL_PARAM_KEYS.contentTypes);

  if (contentTypes) {
    filters.panelFilters.contentTypes = contentTypes.split(',').filter(Boolean);
  }

  const labels = searchParams.get(URL_PARAM_KEYS.labels);

  if (labels) {
    filters.panelFilters.labels = labels.split(',').filter(Boolean);
  }

  const languages = searchParams.get(URL_PARAM_KEYS.languages);

  if (languages) {
    filters.panelFilters.languageDetected = languages;
  }

  const topics = searchParams.get(URL_PARAM_KEYS.topics);

  if (topics) {
    filters.panelFilters.topics = topics.split(',').filter(Boolean);
  }

  // Panel filters - single values (extract datetime-local format from UTC ISO string)
  const startDate = searchParams.get(URL_PARAM_KEYS.startDate);

  if (startDate) {
    const dateTimeLocal = fromISOString(startDate);

    if (dateTimeLocal) {
      filters.panelFilters.startDate = dateTimeLocal;
    }
  }

  const endDate = searchParams.get(URL_PARAM_KEYS.endDate);

  if (endDate) {
    const dateTimeLocal = fromISOString(endDate);

    if (dateTimeLocal) {
      filters.panelFilters.endDate = dateTimeLocal;
    }
  }

  const isSampled = searchParams.get(URL_PARAM_KEYS.isSampled);

  if (isSampled !== null) {
    filters.panelFilters.isSampled = isSampled === 'true';
  }

  // Policy filter
  const policyFilter = searchParams.get(URL_PARAM_KEYS.policyFilter);

  if (policyFilter && (policyFilter === 'compliant' || policyFilter === 'violations')) {
    filters.panelFilters.policyFilter = policyFilter;
  }

  // Metadata (deserialize from JSON)
  const metadata = searchParams.get(URL_PARAM_KEYS.metadata);

  if (metadata) {
    try {
      filters.panelFilters.metadata = JSON.parse(metadata);
    } catch (error) {
      console.warn('Failed to parse metadata from URL:', error);
    }
  }

  // Pagination
  const page = searchParams.get(URL_PARAM_KEYS.page);

  if (page) {
    const pageNum = parseInt(page, 10);

    if (!isNaN(pageNum) && pageNum > 0) {
      pagination.currentPage = pageNum;
    }
  }

  const pageSize = searchParams.get(URL_PARAM_KEYS.pageSize);

  if (pageSize) {
    const pageSizeNum = parseInt(pageSize, 10);

    if (!isNaN(pageSizeNum) && pageSizeNum > 0) {
      pagination.pageSize = pageSizeNum;
    }
  }

  return { filters, pagination };
}

/**
 * Check if the current URL has any search parameters
 */
export function hasSearchParams(searchParams: URLSearchParams): boolean {
  return Array.from(searchParams.keys()).some(key => Object.values(URL_PARAM_KEYS).includes(key as keyof typeof URL_PARAM_KEYS));
}

/**
 * Create a shareable URL with current search filters
 */
export function createShareableUrl(baseUrl: string, filters: SearchFilters, pagination: PaginationConfig): string {
  const params = serializeFiltersToUrl(filters, pagination);
  const url = new URL(baseUrl);

  url.search = params.toString();

  return url.toString();
}
